import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Box,
  IconButton,
  Alert,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import { MuiTelInput } from 'mui-tel-input'
import { useLeads } from '../../hooks/leads/useLeads'
import { useCustomColumns } from '../../hooks/leads/useCustomColumns'
import { LeadSource, LEAD_SOURCE_LABELS } from '../../types/lead'
import { useEditLeadDialog } from '../../hooks/leads/useEditLeadDialog'
import { LeadStatusSelect } from '../leads/status'
import { DynamicFormFields } from '../dynamicFields/DynamicFormFields'

interface AddLeadDialogProps {
  open: boolean
  onClose: () => void
  onComplete: () => void
  smbId: string
  customStatuses?: string[]
  setCustomStatuses?: React.Dispatch<React.SetStateAction<string[]>>
  onStatusUpdated?: (res?: any, createdStatus?: string) => void
}

const AddLeadDialog: React.FC<AddLeadDialogProps> = ({ open, onClose, onComplete, smbId, customStatuses, setCustomStatuses, onStatusUpdated }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    source: LeadSource.DIRECT,
    status: 'New',
    notes: '',
    customFields: {} as Record<string, any>,
  })
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const { customColumns, fetchCustomColumns, addOptionToColumn } = useCustomColumns(smbId)

  useEffect(() => {
    if (open) {
      fetchCustomColumns()
    }
  }, [open, fetchCustomColumns])

  const {
    createLead,
    isLoading,
    nameError,
    phoneError,
    serverError,
    setNameError,
    setPhoneError,
    setServerError,
  } = useLeads({
    smbId,
    onSuccess: () => {
      setFormData({ name: '', phone: '', source: LeadSource.DIRECT, status: 'New', notes: '', customFields: {} })
      onComplete()
    },
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }))
    if (e.target.name === 'name') {
      setNameError(null)
    } else if (e.target.name === 'phone') {
      setPhoneError(null)
    }
    setServerError(null)
  }

  const { openEditLead, renderEditLeadDialog } = useEditLeadDialog({
    onSuccess: () => {
      setFormData({ name: '', phone: '', source: LeadSource.DIRECT, status: 'New', notes: '', customFields: {} })
      onComplete()
      onClose()
    },
    smbId,
    customStatuses,
    setCustomStatuses,
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!smbId) return
    
    const result = await createLead(
      formData.name,
      formData.phone,
      formData.source,
      formData.status,
      formData.notes,
      formData.customFields
    )
    if (result && result.status === 'conflict' && result.existingLead) {
      openEditLead(
        result.existingLead.id,
        result.existingLead.name,
        result.existingLead.phone,
        result.existingLead.source,
        result.existingLead.status,
        'conflict',
        result.inputName,
        result.existingLead.notes,
        result.existingLead.custom_fields,
        result.inputNotes,
        result.inputCustomFields,
        result.inputStatus
      )
    } else if (result && result.status === 'success') {
      onClose()
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      setFormData({ name: '', phone: '', source: LeadSource.DIRECT, status: 'New', notes: '', customFields: {} })
      setNameError(null)
      setPhoneError(null)
      setServerError(null)
      onClose()
    }
  }

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight="bold">
              Add New Lead
            </Typography>
            <IconButton onClick={handleClose} disabled={isLoading}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
          <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {serverError && (
              <Alert severity="error" onClose={() => setServerError(null)}>
                {serverError}
              </Alert>
            )}

            <TextField
              fullWidth
              name="name"
              label="Name"
              value={formData.name}
              onChange={handleChange}
              autoFocus
              error={!!nameError}
              helperText={nameError}
            />
            
            <MuiTelInput
              fullWidth
              name="phone"
              label="Phone Number"
              value={formData.phone}
              onChange={(newPhone) => {
                setFormData(prev => ({ ...prev, phone: newPhone }))
                setPhoneError(null)
                setServerError(null)
              }}
              required
              defaultCountry="IN"
              preferredCountries={['IN', 'US', 'GB', 'CA', 'AU', 'AE', 'SG', 'NZ', 'ZA', 'DE', 'FR', 'IT', 'ES']}
              forceCallingCode
              disableFormatting
              error={!!phoneError}
              helperText={phoneError}
              MenuProps={{
                PaperProps: {
                  sx: { maxHeight: 300 }
                },
                disablePortal: true
              }}
            />
            
            <TextField
              select
              fullWidth
              name="source"
              label="Source"
              value={formData.source}
              onChange={handleChange}
            >
              {Object.entries(LEAD_SOURCE_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
            
            <LeadStatusSelect
              smbId={smbId}
              value={formData.status}
              onChange={(newStatus) => setFormData(prev => ({ ...prev, status: newStatus }))}
              customStatuses={customStatuses}
              setCustomStatuses={setCustomStatuses}
              onStatusUpdated={onStatusUpdated}
            />


            <DynamicFormFields
              customColumns={customColumns}
              notes={formData.notes}
              customFields={formData.customFields}
              onNotesChange={(notesVal) => setFormData((prev) => ({ ...prev, notes: notesVal }))}
              onCustomFieldChange={(key, val) =>
                setFormData((prev) => ({
                  ...prev,
                  customFields: { ...prev.customFields, [key]: val },
                }))
              }
              onAddOption={addOptionToColumn}
            />
          </DialogContent>
          
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading || !formData.phone.trim() || !smbId}
            >
              {isLoading ? 'Adding...' : 'Add Lead'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      {renderEditLeadDialog()}
    </>
  )
}

export default AddLeadDialog
